import { Box, SimpleGrid, Image, AspectRatio, Divider } from "@chakra-ui/react";
import { useRouter } from "next/router";
import {
  Button,
  Container,
  InternalLink,
  PageTitle,
  NextImage,
} from "components";
import { PageSubheading } from "components/molecules/PageSubheading";
import { route } from "config/routes";
import { NavigationPageSpacer } from "components";
import { IIntegration } from "interfaces/integration.interface";
import { getContentfulImageUrl } from "utils/getContentulImageUrl";
import { useCallback, useMemo } from "react";

enum IntegrationSlugs {
  REAPIT = "reapit-integration",
}

const Integration = ({ integration }: { integration: IIntegration }) => {
  const { name, description, logo, slug } = integration;
  const router = useRouter();

  const isReapitIntegration = useMemo(() => {
    return slug === IntegrationSlugs.REAPIT || false;
  }, [slug]);

  const customOnHover = useMemo(() => {
    if (isReapitIntegration) {
      return {
        cursor: "pointer",
        _hover: {
          transform: "scale(1.08)",
        },
      };
    }
  }, [isReapitIntegration]);

  const handleReapitLink = useCallback(() => {
    if (isReapitIntegration) {
      router.push(slug);
    }
  }, [slug]);

  return (
    <Box
      rounded="md"
      border="1px solid"
      borderColor="gray.150"
      overflow="hidden"
      bg="white"
      transition={"transform .2s"}
      maxW="xs"
      mx="auto"
      p={6}
      onClick={handleReapitLink}
      {...customOnHover}
    >
      <AspectRatio ratio={4 / 3}>
        <NextImage alt={'an integration logo'} src={`${getContentfulImageUrl(logo)}`} fill />
      </AspectRatio>
      <Box color="gray.600" textAlign="center" fontSize="sm">
        {integration.description}
      </Box>
    </Box>
  );
};

export const Integrations = ({
  integrations,
}: {
  integrations: IIntegration[];
}) => {
  return (
    <Box bg="gray.50" pt={24}>
      <NavigationPageSpacer />
      <Box
        pt={0}
        textAlign="center"
        justifyContent="center"
        display="flex"
        alignItems="center"
        flexDirection="column"
        mb={6}
      >
        <Container>
          <PageTitle>Integrations</PageTitle>
          <Box maxW="xl" mx="auto">
            <PageSubheading>
              LeadPro integrates with most Estate Agent CRMs, and the majority
              of other software through our Zapier integration.{" "}
            </PageSubheading>
          </Box>
        </Container>
      </Box>
      <Box pb={20}>
        <Container>
          <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={{ base: 4, lg: 8 }}>
            {integrations.map((integration: IIntegration, i: number) => {
              return <Integration integration={integration} key={i} />;
            })}
          </SimpleGrid>

          <Box textAlign="center" mt={12}>
            <Box textAlign="center" fontSize="3xl" mb={6}>
              Need a custom integration?
            </Box>
            <InternalLink href={route("CONTACT_INDEX")}>
              <Button size="lg" isWide>
                Get in touch
              </Button>
            </InternalLink>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
